import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, Typography, IconButton, TextField } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  setRarity,
  setType,
  setItemType,
  setNewItems,
  setEthereal,
  setUnidentified,
  setLimitFilter,
  removeStatFilter,
  updateStatFilter,
  setViewType,
  setPriceFilter,
  removeFilterFromGroup,
  updateFilterInGroup,
} from '../../features/filters/filterSlice';
import { StatFilter } from '../../models/filter';
import { ItemRarity } from '../../models/item';
import { secondaryColor } from '../../assets/jss/material-kit-react';
import config from '../../config/config.json';

// Static translation references for translation detection scripts
// These keys are now used explicitly in conditional statements below
/* 
t('search.viewType.instant') 
t('search.viewType.products')
t('search.priceFilter.priced')
t('search.priceFilter.unpriced')
*/

interface ActiveFiltersProps {
  className?: string;
  hideStatFilters?: boolean;
}

// Helper function to get color from rarity
const getColorFromRarity = (rarName?: string): string => {
  const textColorMap: Record<ItemRarity, string> = {
    WHITE: '#FFFFFF',
    SET: '#00FF00',
    MAGIC: '#4169E1',
    UNIQUE: '#A59263',
    CRAFTED: '#FFA800',
    RARE: '#FFFF00',
    NORMAL: '#FFFFFF',
    RUNEWORD: '#A59263',
    RUNE: '#A59263',
  };

  return textColorMap[rarName?.toUpperCase() as ItemRarity] || '#FFFFFF';
};

interface ActiveFilter {
  type:
    | 'rarity'
    | 'category'
    | 'itemType'
    | 'newItems'
    | 'ethItems'
    | 'unidItems'
    | 'limit'
    | 'viewType'
    | 'priceFilter';
  label: string;
  color?: string;
  category?: any;
  itemTypeData?: any;
}

const ActiveFilters: React.FC<ActiveFiltersProps> = ({ className, hideStatFilters = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get filter states from Redux
  const {
    rarity,
    type,
    itemType,
    newItems,
    ethereal,
    unidentified,
    limitFilter,
    viewType,
    priceFilter,
    statFilters,
    statFilterGroups,
  } = useSelector((state: any) => state.filters);

  // Get static data for filter display
  const { types, categories, rarities } = useSelector((state: any) => state.staticData);

  const activeFilters: ActiveFilter[] = [];

  if (rarity !== '') {
    // Find the rarity by name instead of ID using effective rarities
    const rarityItem =
      rarities && Array.isArray(rarities)
        ? rarities.find((r) => r.name.toLowerCase() === rarity.toLowerCase())
        : null;
    // If rarity is not found in static data, just display the rarity name directly
    if (rarityItem) {
      activeFilters.push({
        type: 'rarity' as const,
        label: rarityItem.name,
        color: getColorFromRarity(rarityItem.name),
      });
    } else if (rarity) {
      activeFilters.push({
        type: 'rarity' as const,
        label: rarity.charAt(0).toUpperCase() + rarity.slice(1),
        color: getColorFromRarity(rarity),
      });
    }
  }

  if (type !== '' && typeof type === 'string' && itemType === '') {
    const category = categories.find((c) => c.name.toLowerCase() === type.toLowerCase());
    if (category) {
      activeFilters.push({
        type: 'category' as const,
        label: category.name,
        category: category,
      });
    } else {
      // If category not found in static data, display the type name directly
      activeFilters.push({
        type: 'category' as const,
        label: type.charAt(0).toUpperCase() + type.slice(1),
      });
    }
  }

  if (itemType !== '' && typeof itemType === 'string') {
    const itemTypeItem = types.find((t) => t.name.toLowerCase() === itemType.toLowerCase());
    if (itemTypeItem) {
      activeFilters.push({
        type: 'itemType' as const,
        label: itemTypeItem.name,
        itemTypeData: itemTypeItem,
      });
    } else {
      // If item type not found in static data, display the itemType name directly
      activeFilters.push({
        type: 'itemType' as const,
        label: itemType.charAt(0).toUpperCase() + itemType.slice(1),
      });
    }
  }

  if (newItems === 'new-items-only') {
    activeFilters.push({
      type: 'newItems' as const,
      label: t('filters.newItemsOnly'),
    });
  }

  if (ethereal !== '') {
    activeFilters.push({
      type: 'ethItems' as const,
      label: ethereal === 'eth' ? t('filters.ethItemsOnly') : t('filters.excludeEthItems'),
    });
  }

  if (unidentified === 'unid') {
    activeFilters.push({
      type: 'unidItems' as const,
      label: t('filters.unidItemsOnly'),
    });
  }

  if (limitFilter !== 100) {
    activeFilters.push({
      type: 'limit' as const,
      label: `${t('filters.searchLimit')}: ${limitFilter}`,
    });
  }

  if (viewType !== 'all') {
    let viewTypeLabel = '';
    if (viewType === 'instant') {
      viewTypeLabel = t('search.viewType.instant');
    } else if (viewType === 'products') {
      viewTypeLabel = t('search.viewType.products');
    }

    activeFilters.push({
      type: 'viewType' as const,
      label: viewTypeLabel,
    });
  }

  if (priceFilter !== 'all') {
    let priceFilterLabel = '';
    if (priceFilter === 'priced') {
      priceFilterLabel = t('search.priceFilter.priced');
    } else if (priceFilter === 'unpriced') {
      priceFilterLabel = t('search.priceFilter.unpriced');
    }

    activeFilters.push({
      type: 'priceFilter' as const,
      label: priceFilterLabel,
    });
  }

  // Use the new statFilterGroups for visibility check
  const hasActiveStatFilters =
    !hideStatFilters &&
    ((statFilters && statFilters.length > 0) || (statFilterGroups && statFilterGroups.length > 0));

  if (activeFilters.length === 0 && !hasActiveStatFilters) return null;

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: 2,
        p: 2,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
      }}
    >
      {activeFilters.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {activeFilters.map((filter, index) => (
            <Chip
              key={`${filter.type}-${index}`}
              label={filter.label}
              onDelete={() => {
                switch (filter.type) {
                  case 'rarity':
                    dispatch(setRarity(''));
                    break;
                  case 'category':
                    dispatch(setType(''));
                    break;
                  case 'itemType':
                    dispatch(setItemType(''));
                    break;
                  case 'newItems':
                    dispatch(setNewItems(''));
                    break;
                  case 'ethItems':
                    dispatch(setEthereal(''));
                    break;
                  case 'unidItems':
                    dispatch(setUnidentified(''));
                    break;
                  case 'limit':
                    dispatch(setLimitFilter(100));
                    break;
                  case 'viewType':
                    dispatch(setViewType('all'));
                    break;
                  case 'priceFilter':
                    dispatch(setPriceFilter('all'));
                    break;
                }
              }}
              avatar={
                filter.color ? (
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: filter.color,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  />
                ) : filter.category ? (
                  // Category images preview (multiple overlapping images)
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      width: '36px',
                      height: '16px',
                      mr: 0.5,
                    }}
                  >
                    {filter.category.types?.slice(0, 3).map((type: any, index: number) => (
                      <img
                        key={type.id}
                        src={`${config.backend_base_url}skin/${type.skin_path}${ethereal === 'eth' ? '_eth' : ''}`}
                        alt={type.name}
                        style={{
                          width: '16px',
                          height: '16px',
                          objectFit: 'contain',
                          position: 'absolute',
                          left: `${index * 8}px`,
                          zIndex: 3 - index,
                        }}
                      />
                    ))}
                  </Box>
                ) : filter.itemTypeData?.skin_path ? (
                  // Single item type image
                  <img
                    src={`${config.backend_base_url}skin/${filter.itemTypeData.skin_path}${ethereal === 'eth' ? '_eth' : ''}`}
                    alt={filter.itemTypeData.name}
                    style={{
                      width: '16px',
                      height: '16px',
                      objectFit: 'contain',
                      marginRight: '4px',
                    }}
                  />
                ) : undefined
              }
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                '& .MuiChip-deleteIcon': {
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : 'rgba(0, 0, 0, 0.7)',
                },
                '& .MuiChip-avatar': {
                  marginLeft: '4px',
                  marginRight: '4px',
                  width: '8px',
                  height: '8px',
                },
                border: `1px solid ${secondaryColor}33`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Legacy stat filters support */}
      {!hideStatFilters && statFilters && statFilters.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('filters.statFilter')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {statFilters.map((filter: StatFilter) => (
              <Box
                key={filter.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '16px',
                  padding: '4px 8px',
                  margin: '4px',
                  border: `1px solid ${secondaryColor}33`,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {filter.name}
                </Typography>
                <TextField
                  label={t('filters.minValue')}
                  id={`min-${filter.id}`}
                  value={filter.min.toString()}
                  onChange={(params) =>
                    dispatch(updateStatFilter({ ...filter, min: parseInt(params.target.value) }))
                  }
                  type="number"
                  inputProps={{
                    min: 1,
                    max: 99999,
                  }}
                  size="small"
                  sx={{ width: '80px' }}
                  margin="dense"
                />
                <TextField
                  label={t('filters.maxValue')}
                  id={`max-${filter.id}`}
                  value={filter.max.toString()}
                  onChange={(params) =>
                    dispatch(updateStatFilter({ ...filter, max: parseInt(params.target.value) }))
                  }
                  type="number"
                  inputProps={{
                    min: 0,
                    max: 99999,
                  }}
                  size="small"
                  sx={{ width: '80px' }}
                  margin="dense"
                />
                <IconButton
                  size="small"
                  onClick={() => dispatch(removeStatFilter(filter.id))}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.7)',
                    '&:hover': {
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.9)'
                          : 'rgba(0, 0, 0, 0.9)',
                    },
                    padding: '4px',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* New stat filter groups support */}
      {!hideStatFilters && statFilterGroups && statFilterGroups.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('filters.statFilter')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {statFilterGroups.map((group, groupIndex) => (
              <Box
                key={group.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                }}
              >
                {groupIndex > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      alignSelf: 'center',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      my: 0.5,
                    }}
                  >
                    AND
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1,
                    borderRadius: '8px',
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${secondaryColor}33`,
                  }}
                >
                  {group.filters.map((filter, filterIndex) => (
                    <Box key={filter.id}>
                      {filterIndex > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            alignSelf: 'center',
                            fontWeight: 'bold',
                            color: 'secondary.main',
                            textAlign: 'center',
                            my: 0.5,
                          }}
                        >
                          OR
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.05)',
                          borderRadius: '16px',
                          padding: '4px 8px',
                          border: `1px solid ${secondaryColor}33`,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {filter.name}
                        </Typography>
                        <TextField
                          label={t('filters.minValue')}
                          id={`min-${group.id}-${filter.id}`}
                          value={filter.min.toString()}
                          onChange={(params) =>
                            dispatch(
                              updateFilterInGroup({
                                groupId: group.id,
                                filter: { ...filter, min: parseInt(params.target.value) },
                              })
                            )
                          }
                          type="number"
                          inputProps={{
                            min: 1,
                            max: 99999,
                          }}
                          size="small"
                          sx={{ width: '80px' }}
                          margin="dense"
                        />
                        <TextField
                          label={t('filters.maxValue')}
                          id={`max-${group.id}-${filter.id}`}
                          value={filter.max.toString()}
                          onChange={(params) =>
                            dispatch(
                              updateFilterInGroup({
                                groupId: group.id,
                                filter: { ...filter, max: parseInt(params.target.value) },
                              })
                            )
                          }
                          type="number"
                          inputProps={{
                            min: 0,
                            max: 99999,
                          }}
                          size="small"
                          sx={{ width: '80px' }}
                          margin="dense"
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            dispatch(
                              removeFilterFromGroup({ groupId: group.id, filterId: filter.id })
                            )
                          }
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.7)'
                                : 'rgba(0, 0, 0, 0.7)',
                            '&:hover': {
                              color: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.9)'
                                  : 'rgba(0, 0, 0, 0.9)',
                            },
                            padding: '4px',
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ActiveFilters;
export { getColorFromRarity };
